import usePos from 'stores/pos';
import { onPageView } from 'shared/gtm';
import { useLocation } from 'react-router-dom';
import { extendedPages } from './helpers';
import { useEffect, useRef } from 'react';
import useCustomer from 'stores/customer';

type PageViewNullishParams<T> = {
	[P in keyof T]: T[P] | null;
};

/**
 * @param params - if some attribute has `null` as value, the event will await for the value to be set before sending the event to GA.
 */
export default function usePageView<T = Record<string, unknown>>(params?: PageViewNullishParams<T>) {
	const prevPage = useRef<string>();
	const location = useLocation();
	const current = location.pathname + location.search;

	const { selectedPos } = usePos();
	const {
		customerStore: {
			data: { accessType, hyperaRole },
		},
	} = useCustomer();

	const role = hyperaRole || accessType || selectedPos?.role;

	useEffect(() => {
		const breakConditions = {
			samePage: current === prevPage.current,
			extendedPageWithoutParams: extendedPages.some((page) => current.includes(page)) && !params,
			wantingSomeParam: params && Object.values(params).some((value) => value === null),
		};

		if (Object.values(breakConditions).some(Boolean)) return;

		onPageView({ current, previous: prevPage.current, role, ...params });

		return () => {
			prevPage.current = current;
		};
	}, [location, params]);
}
